import type { LibraryOp } from "./state";

import type { LibrarySnapshot } from "@/types/library";

import {
  isMovieId,
  isRatingValue,
  parseLibrarySnapshot,
  toIsoTimestamp,
} from "./snapshot";

/**
 * Cross-tab sync: tabs tell each other about confirmed changes and fresh
 * server snapshots, so a change made in one tab shows up in the others
 * immediately. Uses BroadcastChannel, or `storage` events where unavailable.
 */

export type LibraryMessage =
  | { type: "op"; userId: string; op: LibraryOp }
  | {
      type: "snapshot";
      userId: string;
      library: LibrarySnapshot;
      syncedAt: string;
      /** When the sending tab requested it (same device clock), if known. */
      requestedAt: number | null;
    };

export interface LibraryChannel {
  post(message: LibraryMessage): void;
  subscribe(listener: (message: LibraryMessage) => void): () => void;
  close(): void;
}

export const LIBRARY_CHANNEL_NAME = "cage-mdb:library";
const STORAGE_BUS_KEY = "cage-mdb:library-bus";

const isTimestamp = (value: unknown) => toIsoTimestamp(value) !== null;

function parseOp(value: unknown): LibraryOp | null {
  if (!value || typeof value !== "object") return null;
  const op = value as Record<string, unknown>;

  if (!isMovieId(op.movieId)) return null;

  switch (op.type) {
    case "watchlist/add":
    case "watched/add":
      return isTimestamp(op.at) ? (op as unknown as LibraryOp) : null;
    case "watchlist/remove":
    case "watched/remove":
    case "rating/remove":
      return op as unknown as LibraryOp;
    case "rating/set":
      return isTimestamp(op.at) && isRatingValue(op.rating)
        ? (op as unknown as LibraryOp)
        : null;
    case "restore": {
      if (!op.entry || typeof op.entry !== "object") return null;
      const entry = op.entry as Record<
        string,
        Record<string, unknown> | null | undefined
      >;
      const valid =
        (entry.watchlist == null || isTimestamp(entry.watchlist.addedAt)) &&
        (entry.watched == null || isTimestamp(entry.watched.watchedAt)) &&
        (entry.rating == null ||
          (isTimestamp(entry.rating.ratedAt) &&
            isRatingValue(entry.rating.rating)));

      return valid ? (op as unknown as LibraryOp) : null;
    }
    default:
      return null;
  }
}

/** Validate a message from another tab. */
export function parseLibraryMessage(data: unknown): LibraryMessage | null {
  if (!data || typeof data !== "object") return null;
  const message = data as Record<string, unknown>;

  if (typeof message.userId !== "string" || !message.userId) return null;

  if (message.type === "op") {
    const op = parseOp(message.op);

    return op ? { type: "op", userId: message.userId, op } : null;
  }

  if (message.type === "snapshot") {
    const library = parseLibrarySnapshot(message.library);
    const syncedAt = toIsoTimestamp(message.syncedAt);
    const requestedAt =
      typeof message.requestedAt === "number" &&
      Number.isFinite(message.requestedAt)
        ? message.requestedAt
        : null;

    return library && syncedAt
      ? {
          type: "snapshot",
          userId: message.userId,
          library,
          syncedAt,
          requestedAt,
        }
      : null;
  }

  return null;
}

function broadcastChannel(): LibraryChannel {
  const channel = new BroadcastChannel(LIBRARY_CHANNEL_NAME);

  return {
    post(message) {
      try {
        channel.postMessage(message);
      } catch {
        // The channel may already be closed.
      }
    },
    subscribe(listener) {
      const onMessage = (event: MessageEvent) => {
        const message = parseLibraryMessage(event.data);

        if (message) listener(message);
      };

      channel.addEventListener("message", onMessage);

      return () => channel.removeEventListener("message", onMessage);
    },
    close() {
      channel.close();
    },
  };
}

/** Fallback: write messages to localStorage; other tabs get `storage` events. */
function storageChannel(target: Window): LibraryChannel {
  return {
    post(message) {
      try {
        target.localStorage.setItem(
          STORAGE_BUS_KEY,
          // A nonce makes every write a change, so every write is an event.
          JSON.stringify({ message, nonce: Math.random() }),
        );
      } catch {
        // Storage unavailable: no cross-tab sync.
      }
    },
    subscribe(listener) {
      const onStorage = (event: StorageEvent) => {
        if (event.key !== STORAGE_BUS_KEY || !event.newValue) return;
        try {
          const message = parseLibraryMessage(
            (JSON.parse(event.newValue) as { message?: unknown }).message,
          );

          if (message) listener(message);
        } catch {
          // Ignore malformed data.
        }
      };

      target.addEventListener("storage", onStorage);

      return () => target.removeEventListener("storage", onStorage);
    },
    close() {},
  };
}

export function createLibraryChannel(): LibraryChannel | null {
  if (typeof window === "undefined") return null;

  try {
    return typeof BroadcastChannel === "function"
      ? broadcastChannel()
      : storageChannel(window);
  } catch {
    return null;
  }
}
